// SPDX-License-Identifier: AGPL-3.0-or-later

import {GuildBadge} from '@app/features/guild/components/GuildBadge';
import {
	SERVER_TAG_DEFAULT_BADGE_ID,
	SERVER_TAG_DEFAULT_COLOR,
	ServerTagBadgeGlyph,
	colorNumberToCss,
	type ServerTagBadgeId,
} from '@app/features/guild/components/server_tag/ServerTagBadgeIcons';
import styles from '@app/features/guild/components/server_tag/ServerTagGuildProfileCard.module.css';
import GuildCount from '@app/features/guild/state/GuildCount';
import Guilds from '@app/features/guild/state/Guilds';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import type {PopoutKey} from '@app/features/ui/popover';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {getFormattedShortDate} from '@app/features/user/utils/DateFormatting';
import {getCurrentLocale} from '@app/features/user/utils/LocaleUtils';
import {isServerTagBadgeId} from '@fluxer/constants/src/ServerTagConstants';
import type {UserPrimaryGuildResponse} from '@fluxer/schema/src/domains/user/UserResponseSchemas';
import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';
import {msg} from '@lingui/core/macro';
import {Plural, Trans, useLingui} from '@lingui/react/macro';
import {formatNumber} from '@pkgs/number_utils/src/NumberFormatting';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo} from 'react';

// Reuse existing catalog msgids (no msgctxt) so locale packs already include translations.
const ONLINE_DESCRIPTOR = msg({
	message: '{renderedPresenceCount} online',
	comment:
		'Presence summary on a community invite embed. renderedPresenceCount is a formatted count of online members.',
});
const CREATED_LABEL_DESCRIPTOR = msg({
	message: 'Created:',
	comment: 'Label before a creation date.',
});
const GO_TO_COMMUNITY_DESCRIPTOR = msg({
	message: 'Go to community',
	comment: 'Button label on a community invite embed when the current user is already a member.',
});

const BANNER_TINTS = [
	'#a99adf',
	'#93b7e0',
	'#8fc9c6',
	'#9fd49a',
	'#d4c26a',
	'#e0a878',
	'#e08a8a',
	'#d98ab8',
	'#9a8ad9',
	'#8aa0d9',
] as const;

function resolveBadgeId(badgeId: string): ServerTagBadgeId {
	return isServerTagBadgeId(badgeId) ? badgeId : SERVER_TAG_DEFAULT_BADGE_ID;
}

function resolveBannerTint(guildId: string): string {
	const index = Number(BigInt(guildId) % BigInt(BANNER_TINTS.length));
	return BANNER_TINTS[index] ?? BANNER_TINTS[0];
}

export interface ServerTagGuildProfileCardProps {
	primaryGuild: UserPrimaryGuildResponse;
	popoutKey?: PopoutKey;
	onClose: () => void;
}

export const ServerTagGuildProfileCard = observer(function ServerTagGuildProfileCard({
	primaryGuild,
	onClose,
}: ServerTagGuildProfileCardProps) {
	const {i18n} = useLingui();
	const guildId = primaryGuild.identity_guild_id;
	const guild = Guilds.getGuild(guildId);
	const isMember = guild != null;
	const counts = GuildCount.getCounts(guildId);
	const locale = getCurrentLocale();

	useEffect(() => {
		if (isMember) {
			GuildCount.requestCounts(guildId);
		}
	}, [guildId, isMember]);

	const name = guild?.name || primaryGuild.name || primaryGuild.tag;
	const icon = guild?.icon ?? primaryGuild.icon ?? null;
	const iconUrl = icon ? AvatarUtils.getGuildIconURL({id: guildId, icon}, true) : null;
	const bannerHash = guild?.banner ?? null;
	const bannerUrl = bannerHash
		? AvatarUtils.getGuildBannerURL({id: guildId, banner: bannerHash}, true) || null
		: null;
	const initial = name.charAt(0).toUpperCase() || '?';
	const onlineCount = counts?.onlineCount ?? null;
	const memberCount = counts?.memberCount ?? guild?.memberCount ?? null;
	const renderedMemberCount = memberCount != null ? formatNumber(memberCount, locale) : null;
	const createdAt = useMemo(() => {
		if (guild?.createdAt) return guild.createdAt;
		try {
			return new Date(SnowflakeUtils.extractTimestamp(guildId));
		} catch {
			return null;
		}
	}, [guild, guildId]);
	const createdLabel = createdAt ? getFormattedShortDate(createdAt) : null;
	const accent = colorNumberToCss(primaryGuild.color ?? SERVER_TAG_DEFAULT_COLOR);
	const badgeId = resolveBadgeId(primaryGuild.badge_id);

	const handleGoToServer = useCallback(() => {
		if (!isMember) return;
		onClose();
		ModalCommands.popAll();
		const selectedChannel = SelectedChannel.selectedChannelIds.get(guildId);
		NavigationCommands.selectGuild(guildId, selectedChannel);
	}, [guildId, isMember, onClose]);

	return (
		<div className={styles.card} data-flx="guild.server-tag-guild-profile-card.card">
			<div className={styles.banner} data-flx="guild.server-tag-guild-profile-card.banner">
				{bannerUrl ? (
					<div
						className={styles.bannerImage}
						style={{backgroundImage: `url(${bannerUrl})`}}
						aria-hidden
					/>
				) : (
					<div
						className={styles.bannerTint}
						style={{background: resolveBannerTint(guildId)}}
						aria-hidden
					/>
				)}
			</div>
			<div className={styles.body} data-flx="guild.server-tag-guild-profile-card.body">
				<div className={styles.iconRow} data-flx="guild.server-tag-guild-profile-card.icon-row">
					<div className={styles.iconFrame} aria-hidden>
						{iconUrl ? (
							<img className={styles.iconImage} src={iconUrl} alt="" />
						) : (
							<div className={styles.iconPlaceholder}>{initial}</div>
						)}
					</div>
				</div>
				<div className={styles.nameRow} data-flx="guild.server-tag-guild-profile-card.name-row">
					<h2 className={styles.name}>{name}</h2>
					{guild ? <GuildBadge features={guild.features} /> : null}
				</div>
				{(onlineCount != null || memberCount != null) && (
					<ul className={styles.stats} data-flx="guild.server-tag-guild-profile-card.stats">
						{onlineCount != null ? (
							<li className={styles.stat}>
								<span className={`${styles.statDot} ${styles.statDotOnline}`} aria-hidden />
								<span>
									{i18n._(ONLINE_DESCRIPTOR, {
										renderedPresenceCount: formatNumber(onlineCount, locale),
									})}
								</span>
							</li>
						) : null}
						{memberCount != null && renderedMemberCount != null ? (
							<li className={styles.stat}>
								<span className={`${styles.statDot} ${styles.statDotMembers}`} aria-hidden />
								<span>
									<Trans>
										{renderedMemberCount}{' '}
										<Plural value={memberCount} one="member" other="members" />
									</Trans>
								</span>
							</li>
						) : null}
					</ul>
				)}
				{createdLabel ? (
					<p className={styles.created}>
						{i18n._(CREATED_LABEL_DESCRIPTOR)} {createdLabel}
					</p>
				) : null}
				{primaryGuild.tag ? (
					<div className={styles.tagRow} data-flx="guild.server-tag-guild-profile-card.tag-row">
						<span className={styles.tagPill}>
							<ServerTagBadgeGlyph badgeId={badgeId} size={12} accent={accent} />
							<span className={styles.tagPillText}>{primaryGuild.tag}</span>
						</span>
					</div>
				) : null}
				{isMember ? (
					<div className={styles.actions} data-flx="guild.server-tag-guild-profile-card.actions">
						<Button
							variant="primary"
							fitContainer
							className={styles.goButton}
							onClick={handleGoToServer}
							data-flx="guild.server-tag-guild-profile-card.go-button"
						>
							{i18n._(GO_TO_COMMUNITY_DESCRIPTOR)}
						</Button>
					</div>
				) : null}
			</div>
		</div>
	);
});
